import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  SearchX,
  Loader2,
  AlertCircle,
  Info,
  CheckCircle2,
  XCircle,
  ExternalLink,
} from 'lucide-react';
import { Card, CardTitle, CardDescription, Input, Button, Skeleton } from '@/components/ui';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useLocalStorageState } from '@/lib/useLocalStorageState';

/**
 * Phase 57: 블로그 누락 검출
 * 내 글 URL + 타깃 키워드 → 네이버 검색 색인/노출 여부 판정
 */
export function MissingCheck() {
  const [url, setUrl] = useLocalStorageState('missing.url', '');
  const [kwText, setKwText] = useLocalStorageState('missing.kw', '');
  const [channel, setChannel] = useState<'view' | 'blog' | 'web'>('view');

  const m = useMutation({
    mutationFn: () => {
      const keywords = kwText
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, 10);
      return api.missing.check({ url: url.trim(), keywords, channel, maxPage: 3 });
    },
    onError: (e: Error) => alert(`누락 검출 실패: ${e.message}`),
  });

  const kwCount = kwText.split(/[\n,]/).map((s) => s.trim()).filter(Boolean).length;
  const canSubmit = !!url.trim() && kwCount > 0 && !m.isPending;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-extrabold flex items-center gap-2">
          <SearchX className="h-7 w-7 text-gold" />
          <span className="text-gold-gradient">블로그 누락 검출</span>
          <span className="ml-2 rounded-full bg-gold/15 px-2 py-0.5 text-[11px] font-bold text-gold">
            NEW
          </span>
        </h1>
        <p className="mt-1 text-sm text-gray-400">
          내 글이 네이버 검색에 제대로 노출되는지 / 누락됐는지 키워드별로 확인합니다.
        </p>
      </header>

      <Card className="border-amber-400/30 bg-amber-400/5">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 shrink-0 text-amber-300 mt-0.5" />
          <p className="text-xs text-amber-100/80 leading-relaxed">
            글 URL과 그 글이 노려야 할 키워드들을 입력하면, 각 키워드로 네이버 검색 시 1~3페이지(30위)
            안에 내 글이 노출되는지 확인합니다. <strong>모든 키워드에서 안 보이면 색인 누락 의심</strong>입니다.
          </p>
        </div>
      </Card>

      <Card>
        <CardTitle>검사 정보 입력</CardTitle>
        <form
          className="mt-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) m.mutate();
          }}
        >
          <label className="block">
            <span className="text-xs text-gray-400">내 블로그 글 URL</span>
            <Input
              className="mt-1"
              placeholder="예: https://blog.naver.com/myblog/223456789"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              autoComplete="off"
            />
          </label>
          <label className="block">
            <span className="text-xs text-gray-400">타깃 키워드 (줄바꿈/쉼표 구분, 최대 10개)</span>
            <textarea
              rows={4}
              className="mt-1 w-full rounded-lg border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-white"
              placeholder={'예: 제주도 여행 코스\n제주 맛집 추천\n제주 렌트카'}
              value={kwText}
              onChange={(e) => setKwText(e.target.value)}
            />
          </label>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">검색 영역:</span>
            {(['view', 'blog', 'web'] as const).map((ch) => (
              <button
                key={ch}
                type="button"
                onClick={() => setChannel(ch)}
                className={cn(
                  'rounded-lg border px-3 py-1 text-xs font-semibold',
                  channel === ch
                    ? 'border-gold/60 bg-gold/15 text-gold'
                    : 'border-gray-800 text-gray-400 hover:text-white',
                )}
              >
                {ch === 'view' ? 'VIEW(통합)' : ch === 'blog' ? '블로그' : '웹'}
              </button>
            ))}
          </div>
          <div className="pt-2 flex items-center gap-3">
            <Button type="submit" variant="gold" disabled={!canSubmit}>
              {m.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> 검사 중… ({kwCount}개)
                </>
              ) : (
                <>
                  <SearchX className="h-4 w-4" /> 누락 검출 시작
                </>
              )}
            </Button>
            {m.isPending && (
              <span className="text-xs text-gray-500">키워드당 1~3페이지 확인 — 키워드 수만큼 시간 소요</span>
            )}
          </div>
        </form>

        {m.isError && (
          <p className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            <AlertCircle className="inline h-4 w-4 mr-1" />
            {(m.error as Error).message}
          </p>
        )}
      </Card>

      {m.isPending && (
        <Card>
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        </Card>
      )}

      {m.data && <ResultSection data={m.data} />}
    </div>
  );
}

function ResultSection({ data }: { data: NonNullable<ReturnType<typeof useMutation<Awaited<ReturnType<typeof api.missing.check>>>>['data']> }) {
  const v = data.summary.verdict;
  const verdictBox =
    v === 'all_missing'
      ? { cls: 'border-red-500/40 bg-red-500/10 text-red-200', msg: '🚨 모든 키워드에서 노출 안 됨 — 색인 누락 의심. 글 재발행 또는 수동 색인 요청을 고려하세요.' }
      : v === 'partial'
      ? { cls: 'border-amber-400/40 bg-amber-400/10 text-amber-200', msg: '⚠️ 일부 키워드만 노출됨. 누락된 키워드는 본문 보강이 필요합니다.' }
      : { cls: 'border-emerald-400/40 bg-emerald-400/10 text-emerald-200', msg: '✅ 모든 키워드에서 노출 중! 색인 정상.' };

  return (
    <div className="space-y-4">
      <Card className={cn('border', verdictBox.cls)}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="text-sm font-bold">{verdictBox.msg}</span>
          <span className="text-xs opacity-80">
            노출 {data.summary.indexed} / 누락 {data.summary.missing} · {data.checkedRange} 범위
          </span>
        </div>
      </Card>

      <Card>
        <CardTitle>키워드별 결과</CardTitle>
        <CardDescription>{data.url}</CardDescription>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-800">
                <th className="py-2 pr-2 font-medium">키워드</th>
                <th className="py-2 pr-2 font-medium text-center">상태</th>
                <th className="py-2 pr-2 font-medium text-right">순위</th>
                <th className="py-2 font-medium text-right">분석</th>
              </tr>
            </thead>
            <tbody>
              {data.results.map((r) => (
                <tr key={r.keyword} className="border-b border-gray-900 last:border-b-0 hover:bg-gray-950/60">
                  <td className="py-2.5 pr-2 font-medium text-white">{r.keyword}</td>
                  <td className="py-2.5 pr-2 text-center">
                    {r.indexed ? (
                      <span className="inline-flex items-center gap-1 text-emerald-300 text-xs font-bold">
                        <CheckCircle2 className="h-4 w-4" /> 노출
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-300 text-xs font-bold">
                        <XCircle className="h-4 w-4" /> 누락
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 pr-2 text-right tabular">
                    {r.position != null ? (
                      <span className="text-gold font-bold">{r.position}위</span>
                    ) : (
                      <span className="text-gray-600">-</span>
                    )}
                    {r.page != null && <span className="text-[11px] text-gray-500 ml-1">({r.page}p)</span>}
                  </td>
                  <td className="py-2.5 text-right">
                    <Link
                      to={`/analyze/${encodeURIComponent(r.keyword)}`}
                      className="text-xs text-gold hover:underline inline-flex items-center gap-0.5"
                    >
                      키워드 <ExternalLink className="h-3 w-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
